import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components';
import { ApiClient } from '@utils/apiClient';
import { useToast } from '@contexts/useToast';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';
import { scenarioTestSessionBody } from '@utils/scenarioTestCaller';
import { useAuth } from '@pages/Auth';
import { AgentTestOverlay } from './AgentTestOverlay';

interface TestAgentButtonProps {
  agentId: string;
  agentName: string;
  disabled?: boolean;
  testing?: boolean;
  onTestingChange?: (testing: boolean) => void;
  stopTestRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onSave?: () => Promise<void>;
}

interface TestSessionResponse {
  conversation_id: string;
  ws_url: string;
}

export const TestAgentButton: React.FC<TestAgentButtonProps> = ({
  agentId,
  agentName: _agentName,
  disabled = false,
  testing: _externalTesting,
  onTestingChange: _onTestingChange,
  stopTestRef,
  onSave,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { organization } = useAuth();
  const [internalTesting, setInternalTesting] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const testing = _externalTesting !== undefined ? _externalTesting : internalTesting;
  const setTesting = (value: boolean) => {
    if (_externalTesting === undefined) {
      setInternalTesting(value);
    }
    _onTestingChange?.(value);
  };
  const [connectionStatus, setConnectionStatus] = useState<
    'idle' | 'connecting' | 'connected' | 'disconnected'
  >('idle');
  const [isClosing, setIsClosing] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackAudioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);
  const inputRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const playbackGainNodeRef = useRef<GainNode | null>(null);
  const audioProcessingActiveRef = useRef<boolean>(false);
  const trackMonitorIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const lastAudioReceivedRef = useRef<number>(0);
  const audioGapCheckIntervalRef = useRef<number | null>(null);
  const audioGapTimeoutRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const assistantAudioSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const vadThresholdRef = useRef<number>(0.015); // Voice activity detection threshold
  const lastSpeechTimeRef = useRef<number>(0);
  const isClosingRef = useRef<boolean>(false); // Track if we're in the process of closing
  const forceCloseRef = useRef<boolean>(false);
  const nextPlaybackTimeRef = useRef<number>(0); // Track next scheduled playback time

  const floatTo16BitPCM = (float32Array: Float32Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;

    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    return buffer;
  };

  const playAudioFromAzure = async (audioBuffer: ArrayBuffer) => {
    lastAudioReceivedRef.current = Date.now();
    audioQueueRef.current.push(audioBuffer);

    if (!isPlayingRef.current) {
      processAudioQueue();
    }
  };

  const stopAssistantAudio = () => {
    // Stop all assistant audio sources immediately (for interruptions)
    const sourcesToStop = [...assistantAudioSourcesRef.current];
    assistantAudioSourcesRef.current = [];

    sourcesToStop.forEach(source => {
      // Null out onended BEFORE stopping so the Web Audio API's async onended
      // callback (which fires even for force-stopped nodes) doesn't trigger
      // processAudioQueue() calls that race against new-response audio playback
      // and cause the second utterance to play twice.
      source.onended = null;
      try {
        source.stop(0);
      } catch (e) {
        // Source may already be stopped or in invalid state - ignore
      }
    });

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);

    // Reset playback timing
    nextPlaybackTimeRef.current = 0;
  };

  const detectSpeech = (audioData: Float32Array): boolean => {
    // Calculate RMS (Root Mean Square) for voice activity detection
    let sumSquares = 0;
    for (let i = 0; i < audioData.length; i++) {
      sumSquares += audioData[i] * audioData[i];
    }
    const rms = Math.sqrt(sumSquares / audioData.length);
    return rms > vadThresholdRef.current;
  };

  const processAudioQueue = async () => {
    if (isPlayingRef.current) {
      return;
    }

    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return;
    }

    isPlayingRef.current = true;
    setIsSpeaking(true);

    let audioContext = playbackAudioContextRef.current;

    if (!audioContext || audioContext.state === 'closed') {
      const AudioContextClass =
        window.AudioContext ||
        // @ts-expect-error - webkitAudioContext is a Safari-specific property
        (window as Window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioContext = new AudioContextClass({
        sampleRate: 24000,
      });
      playbackAudioContextRef.current = audioContext;

      const gainNode = audioContext.createGain();
      // Slight volume boost to compensate for µ-law dynamics
      gainNode.gain.value = 1.2;
      playbackGainNodeRef.current = gainNode;
    }

    try {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const audioBuffer = audioQueueRef.current.shift();
      if (!audioBuffer) {
        isPlayingRef.current = false;
        processAudioQueue();
        return;
      }

      const pcmData = new Int16Array(audioBuffer);
      const floatData = new Float32Array(pcmData.length);

      for (let i = 0; i < pcmData.length; i++) {
        floatData[i] = pcmData[i] / 32768.0;
      }

      if (floatData.length === 0) {
        isPlayingRef.current = false;
        processAudioQueue();
        return;
      }

      let sampleRate = 24000;
      let buffer: AudioBuffer;

      try {
        buffer = audioContext.createBuffer(1, floatData.length, sampleRate);
      } catch (err) {
        sampleRate = 16000;
        buffer = audioContext.createBuffer(1, floatData.length, sampleRate);
      }

      buffer.copyToChannel(floatData, 0);

      // Apply crossfade to prevent clicks and pops
      const channelData = buffer.getChannelData(0);
      const fadeLength = Math.min(50, Math.floor(channelData.length * 0.03)); // 3% or 50 samples - shorter for less impact

      // Fade in at start
      for (let i = 0; i < fadeLength; i++) {
        channelData[i] *= i / fadeLength;
      }

      // Fade out at end
      for (let i = 0; i < fadeLength; i++) {
        const idx = channelData.length - fadeLength + i;
        channelData[idx] *= 1 - i / fadeLength;
      }

      const source = audioContext.createBufferSource();
      source.buffer = buffer;

      if (playbackGainNodeRef.current) {
        source.connect(playbackGainNodeRef.current);
        playbackGainNodeRef.current.connect(audioContext.destination);
      } else {
        source.connect(audioContext.destination);
      }

      // Schedule audio for seamless playback (prevents gaps and clicks)
      const currentTime = audioContext.currentTime;
      const bufferDuration = buffer.duration;

      // If we have a next playback time and it's in the future, use it
      // Otherwise start immediately
      let startTime = currentTime;
      if (nextPlaybackTimeRef.current > currentTime) {
        startTime = nextPlaybackTimeRef.current;
      }

      // Start audio at scheduled time
      source.start(startTime);

      // Update next playback time for seamless chain
      nextPlaybackTimeRef.current = startTime + bufferDuration;

      // Track audio source for interruption handling
      assistantAudioSourcesRef.current.push(source);

      source.onended = () => {
        // Remove source from array when it finishes
        assistantAudioSourcesRef.current = assistantAudioSourcesRef.current.filter(
          s => s !== source
        );

        // Continue processing queue
        if (audioQueueRef.current.length > 0) {
          isPlayingRef.current = false;
          processAudioQueue();
        } else {
          isPlayingRef.current = false;
          setIsSpeaking(false);
        }
      };
    } catch (err) {
      console.error('Error playing audio:', err);
      isPlayingRef.current = false;
      if (audioQueueRef.current.length > 0) {
        processAudioQueue();
      }
    }
  };

  const waitForAudioToFinish = (): Promise<void> => {
    return new Promise(resolve => {
      // Check if there's any audio playing or queued
      const hasAudio =
        isPlayingRef.current ||
        audioQueueRef.current.length > 0 ||
        assistantAudioSourcesRef.current.length > 0;

      if (!hasAudio) {
        resolve();
        return;
      }

      // Poll every 100ms to check if audio is done
      const checkInterval = setInterval(() => {
        const stillPlaying =
          isPlayingRef.current ||
          audioQueueRef.current.length > 0 ||
          assistantAudioSourcesRef.current.length > 0;

        if (!stillPlaying) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Maximum wait time: 10 seconds (safety timeout)
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 10000);
    });
  };

  const handleWebSocketMessage = (message: { type: string; [key: string]: unknown }) => {
    // Handle Azure OpenAI Realtime API events
    switch (message.type) {
      case 'input_audio_buffer.speech_started':
        // User started speaking - interrupt assistant audio immediately (server-side detection)
        stopAssistantAudio();
        break;
      case 'response.output_audio_transcript.done':
      case 'response.done':
        break;
      case 'conversation.item.input_audio_transcription.completed':
      case 'conversation.item.created':
        break;
      case 'error':
        console.error('[AUDIO] Error from server:', JSON.stringify(message, null, 2));
        break;
      default:
        break;
    }
  };

  const normalizeWebSocketUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      if (window.location.protocol === 'https:') {
        urlObj.protocol = 'wss:';
      } else {
        urlObj.protocol = 'ws:';
      }
      return urlObj.toString();
    } catch {
      if (window.location.protocol === 'https:') {
        return url.replace(/^ws:/, 'wss:');
      }
      return url.replace(/^wss:/, 'ws:');
    }
  };

  const startTest = async () => {
    try {
      // Save agent settings before starting test
      if (onSave) {
        try {
          await onSave();
        } catch (error) {
          showToast({
            type: 'error',
            title: t('agents.testSessionError'),
            message: error instanceof Error ? error.message : 'Failed to save settings',
          });
          return;
        }
      }

      setTesting(true);
      setConnectionStatus('connecting');
      setShowOverlay(true);

      const response = await ApiClient.post(
        `/agents/agents/${agentId}/test-session/`,
        scenarioTestSessionBody(agentId)
      );
      const result = await handleApiResult<TestSessionResponse>(
        response,
        'Failed to create test session'
      );

      if (!result.success || !result.data) {
        const errorMessage = result.errors?.generalError || 'Failed to create test session';
        showToast({
          type: 'error',
          title: t('agents.testSessionError'),
          message: errorMessage,
        });
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false);
        return;
      }

      const { ws_url } = result.data;

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true, // Browser-level echo cancellation
            noiseSuppression: true, // Browser-level noise suppression
            autoGainControl: true, // Browser-level auto gain control
          },
        });

        streamRef.current = stream;

        stream.getAudioTracks().forEach(track => {
          track.enabled = true;

          track.addEventListener('ended', () => {
            console.error('[MIC] Audio track ended unexpectedly');
          });

          track.addEventListener('mute', () => {
            console.error('[MIC] Audio track muted by browser/system');
          });

          trackMonitorIntervalRef.current = setInterval(() => {
            if (audioProcessingActiveRef.current) {
              if (track.readyState !== 'live' || track.muted || !track.enabled) {
                if (!track.enabled) {
                  track.enabled = true;
                }
              }
            }
          }, 5000);
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Microphone access denied. Please allow microphone access and try again.';
        showToast({
          type: 'error',
          title: t('agents.microphoneAccessError'),
          message: errorMessage,
        });
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false);
        return;
      }

      if (!streamRef.current) {
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false);
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        return;
      }

      const storedStream = streamRef.current;
      const audioTrack = storedStream.getAudioTracks()[0];

      if (!audioTrack || audioTrack.readyState === 'ended') {
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false);
        storedStream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        return;
      }

      const AudioContextClass =
        window.AudioContext ||
        // @ts-expect-error - webkitAudioContext is a Safari-specific property
        (window as Window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      audioContext.addEventListener('statechange', () => {
        if (audioContext.state === 'closed' && testing) {
          console.warn('[MIC] AudioContext was closed during active session');
        }
      });

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Load audio worklet processor from public assets (satisfies CSP requirements)
      try {
        const workletUrl = new URL(
          '/audio-worklets/audioWorkletProcessor.js',
          window.location.origin
        ).toString();
        await audioContext.audioWorklet.addModule(workletUrl);
      } catch (err) {
        console.error('[MIC] Failed to load audio worklet:', err);
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false);
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        return;
      }

      // Create audio processing chain - raw audio, no processing
      const input = audioContext.createMediaStreamSource(storedStream);
      inputRef.current = input;

      const processor = new AudioWorkletNode(audioContext, 'audio-worklet-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1,
      });
      processorRef.current = processor;

      // Connect input to processor
      input.connect(processor);

      // Create dummy destination to keep audio graph active (not connected to speakers)
      const dummyDestination = audioContext.createMediaStreamDestination();
      processor.connect(dummyDestination);

      if (audioContext.state !== 'running') {
        await audioContext.resume();
      }

      // Create WebSocket connection
      const normalizedWsUrl = normalizeWebSocketUrl(ws_url);
      const socket = new WebSocket(normalizedWsUrl);
      socket.binaryType = 'arraybuffer';
      socketRef.current = socket;

      socket.onopen = async () => {
        lastAudioReceivedRef.current = Date.now();

        setConnectionStatus('connected');
        showToast({
          type: 'success',
          title: t('agents.testSessionConnected'),
          message: t('agents.testSessionConnectedMessage'),
          duration: 3000,
        });

        // Ensure AudioContext is running
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        if (audioContext.state !== 'running') {
          try {
            await audioContext.resume();
          } catch (err) {
            console.error('[MIC] Failed to resume AudioContext:', err);
          }
        }

        // Monitor AudioContext state
        audioContext.addEventListener('statechange', () => {
          if (audioProcessingActiveRef.current) {
            if (audioContext.state === 'suspended') {
              audioContext.resume().catch(err => {
                console.error('[MIC] Failed to auto-resume AudioContext:', err);
              });
            }
          }
        });

        // Verify stream is still active
        if (!storedStream.active) {
          console.error('[MIC] Stream is not active');
          return;
        }

        // Mark audio processing as active
        audioProcessingActiveRef.current = true;

        // Enable audio processing in worklet - this starts sending raw audio
        processor.port.postMessage({
          type: 'socket-ready',
          ready: true,
        });

        // Set up handler to receive raw audio from worklet and send to server
        // Raw audio: Float32Array -> Int16 PCM -> WebSocket
        processor.port.onmessage = event => {
          if (event.data.type === 'audio-data' && socket.readyState === WebSocket.OPEN) {
            const inputData = event.data.data; // Float32Array from microphone

            // Verify we have valid audio data
            if (!inputData || inputData.length === 0) {
              return;
            }

            // Client-side VAD: Detect if user is speaking while assistant is playing
            // This provides immediate interruption even if server event is delayed
            if (isPlayingRef.current && detectSpeech(inputData)) {
              const now = Date.now();
              // Only interrupt if we haven't interrupted recently (avoid false positives)
              if (now - lastSpeechTimeRef.current > 100) {
                lastSpeechTimeRef.current = now;
                stopAssistantAudio();
              }
            }

            // Convert Float32 -> Int16 PCM (raw, no processing)
            const pcm = floatTo16BitPCM(inputData);

            // Send raw PCM to server
            try {
              socket.send(pcm);
            } catch (err) {
              console.error('[MIC] Error sending audio:', err);
            }
          }
        };
      };

      socket.onmessage = async event => {
        if (event.data instanceof ArrayBuffer) {
          // Binary audio data from Azure
          lastAudioReceivedRef.current = Date.now();
          await playAudioFromAzure(event.data);
        } else if (event.data instanceof Blob) {
          // Blob audio data
          event.data
            .arrayBuffer()
            .then(async buffer => {
              await playAudioFromAzure(buffer);
            })
            .catch(err => {
              console.error('Error converting blob to buffer:', err);
            });
        } else if (typeof event.data === 'string') {
          // JSON messages from Azure (events, status, etc.)
          try {
            const message = JSON.parse(event.data);
            handleWebSocketMessage(message);
          } catch (err) {
            // Ignore parse errors
          }
        }
      };

      socket.onerror = error => {
        console.error('WebSocket error:', error);
        const errorMessage = 'Connection error. Please try again.';
        showToast({
          type: 'error',
          title: t('agents.testSessionError'),
          message: errorMessage,
        });
        setConnectionStatus('disconnected');
      };

      socket.onclose = async event => {
        setConnectionStatus('disconnected');

        // If we're already closing or forcing close, don't wait again
        if (isClosingRef.current || forceCloseRef.current) {
          cleanup();
          setTesting(false);
          setIsClosing(false);
          setShowOverlay(false);
          forceCloseRef.current = false;
          isClosingRef.current = false;
          return;
        }

        isClosingRef.current = true;
        setIsClosing(true);

        // Wait for audio to finish before cleaning up
        await waitForAudioToFinish();

        cleanup();
        setTesting(false);
        isClosingRef.current = false;
        setIsClosing(false);
        setShowOverlay(false);
        forceCloseRef.current = false;

        if (event.code !== 1000) {
          showToast({
            type: 'info',
            title: t('agents.testSessionClosed'),
            message: t('agents.testSessionClosedMessage'),
            duration: 3000,
          });
        }
      };

      // Store stopTest function in shared ref so other button instances can stop the test
      if (stopTestRef) {
        stopTestRef.current = stopTest;
      }
    } catch (err) {
      console.error('Error starting test:', err);
      const processedErrors = handleApiCatch(err, 'Failed to start test');
      const errorMessage = processedErrors.generalError || 'Failed to start test';
      showToast({
        type: 'error',
        title: t('agents.testSessionError'),
        message: errorMessage,
      });
      setTesting(false);
      setConnectionStatus('idle');
      setShowOverlay(false);
      cleanup();
    }
  };

  const stopTest = async (options?: { immediate?: boolean }) => {
    const immediate = options?.immediate ?? false;

    // Use ref instead of state to avoid stale closure issues
    // (stopTest may be called from a closure captured before ws state was set)
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    setIsClosing(true);

    // Use socketRef.current instead of ws state — socketRef is set synchronously
    // in startTest before setWs(socket), so it's always current even in stale closures.
    const currentWs = socketRef.current;
    if (!currentWs) {
      cleanup();
      setTesting(false);
      setConnectionStatus('idle');
      isClosingRef.current = false;
      setIsClosing(false);
      setShowOverlay(false);
      return;
    }

    try {
      forceCloseRef.current = immediate;

      if (immediate) {
        stopAssistantAudio();
        audioQueueRef.current = [];
        assistantAudioSourcesRef.current = [];
        isPlayingRef.current = false;
      } else {
        // Wait for audio to finish before closing
        await waitForAudioToFinish();
      }

      currentWs.close(1000, 'User stopped test');
      setTesting(false);
      setConnectionStatus('idle');
      setShowOverlay(false);

      // Clear shared ref
      if (stopTestRef) {
        stopTestRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping test:', err);
      isClosingRef.current = false;
      forceCloseRef.current = false;
      setIsClosing(false);
    }
  };

  const cleanup = () => {
    if (audioGapCheckIntervalRef.current !== null) {
      clearInterval(audioGapCheckIntervalRef.current);
      audioGapCheckIntervalRef.current = null;
    }

    if (audioGapTimeoutRef.current !== null) {
      clearTimeout(audioGapTimeoutRef.current);
      audioGapTimeoutRef.current = null;
    }

    // Stop all audio sources
    stopAssistantAudio();

    audioProcessingActiveRef.current = false;

    if (trackMonitorIntervalRef.current) {
      clearInterval(trackMonitorIntervalRef.current);
      trackMonitorIntervalRef.current = null;
    }

    if (inputRef.current) {
      try {
        inputRef.current.disconnect();
      } catch (err) {
        // Ignore
      }
      inputRef.current = null;
    }

    if (processorRef.current) {
      try {
        processorRef.current.port.postMessage({
          type: 'socket-ready',
          ready: false,
        });
        processorRef.current.disconnect();
      } catch (err) {
        // Ignore
      }
      processorRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {
        // Ignore
      });
      audioContextRef.current = null;
    }

    if (streamRef.current && !audioProcessingActiveRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanup();
      if (socketRef.current) {
        socketRef.current.close();
      }
      if (playbackAudioContextRef.current && playbackAudioContextRef.current.state !== 'closed') {
        playbackAudioContextRef.current.close().catch(() => {
          // Ignore
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getButtonText = () => {
    if (connectionStatus === 'connecting') {
      return t('agents.testConnecting');
    }
    if (connectionStatus === 'connected') {
      return t('agents.testStop');
    }
    return t('agents.testAgent');
  };

  // Only disable if we explicitly know balance is insufficient
  // If has_sufficient_balance is undefined or null, assume sufficient (default active)
  const hasInsufficientBalance = organization?.has_sufficient_balance === false;
  const isButtonDisabled = disabled || !agentId || isClosing || hasInsufficientBalance;

  const handleClick = async () => {
    if (testing) {
      // Use shared stop function if available, otherwise use local
      const stopFn = stopTestRef?.current || stopTest;
      await stopFn({ immediate: true });
    } else {
      await startTest();
    }
  };

  const handleOverlayClose = async () => {
    const stopFn = stopTestRef?.current || stopTest;
    await stopFn({ immediate: true });
  };

  return (
    <>
      <Button
        variant={testing ? 'danger' : 'gradient-multi'}
        size="md"
        onClick={handleClick}
        disabled={isButtonDisabled}
        loading={connectionStatus === 'connecting'}
        leftIcon={<span className="material-icons text-sm">{testing ? 'stop' : 'mic'}</span>}
      >
        {getButtonText()}
      </Button>

      <AgentTestOverlay
        isOpen={showOverlay}
        isConnected={connectionStatus === 'connected'}
        isSpeaking={isSpeaking}
        onClose={handleOverlayClose}
      />
    </>
  );
};
